import React, {useEffect, useLayoutEffect, useMemo, useRef} from "react";
import {Button, Icon, Modal, Table} from "semantic-ui-react";
import styles from "./RiskTable.module.scss";
import FirebaseUsage from "../../../../firebase/firebase.usage";
import {RiskModel} from "../../../../models/responses/risk.model";
import {useActiveProjectSelector, useProjectRiskSelector} from "../../../../store/selectors/project.selectors";
import {COLLECTIONS} from "../../../../firebase/constants";
import {useSearchParamatersSelector} from "../../../../store/selectors/search.selectors";
import {useUserSelector} from "../../../../store/selectors/authorization.selectors";
import RiskUploadModal from "./components/RiskUploadModal";
import {useDispatch} from "react-redux";
import * as projectActions from "../../../../store/actions/project.actions";

export default function RiskTable() {
    const risks = useProjectRiskSelector();
    const activeProject = useActiveProjectSelector();
    const searchParams = useSearchParamatersSelector();
    const user = useUserSelector();
    const [addingRisk, setAddingRisk] = React.useState(false);
    const [editingRisk, setEditingRisk] = React.useState(false);
    const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
    const mitigationDescriptionInputRef = useRef<HTMLTextAreaElement>(null);
    const [selectedRisk, setSelectedRisk] = React.useState<RiskModel | null>(null);
    const [showUploadModal, setShowUploadModal] = React.useState(false);
    const [showCreateModal, setShowCreateModal] = React.useState(false);
    const [uploadId, setUploadId] = React.useState<string>('uum9uFbrntSHUP6eJUxm');
    const [newRisk, setNewRisk] = React.useState({
        userReference: 'Risk-001',
        description: '',
        probability: 100,
        distribution: 'triangular',
        scheduleImpact: false,
        scheduleImpactRelative: true,
        costImpact: false,
        costImpactRelative: true,
        scheduleMin: 100,
        scheduleLikely: 150,
        scheduleMax: 200,
        costMin: 100,
        costLikely: 150,
        costMax: 200,
        mitigation: false,
        contractId: activeProject!.contractId,
        currency: 'GBP',
    });
    const [newMitigation, setNewMitigation] = React.useState({
        description: '',
        probability: 100,
        distribution: 'triangular',
        scheduleImpact: false,
        scheduleImpactRelative: true,
        costImpact: false,
        costImpactRelative: true,
        scheduleMin: 100,
        scheduleLikely: 150,
        scheduleMax: 200,
        costMin: 100,
        costLikely: 150,
        costMax: 200,
    });
    const riskRegisterFile = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();

    function useAutosizeTextArea(
        textAreaRef: React.RefObject<HTMLTextAreaElement>,
        value: string
    ) {
        useLayoutEffect(() => {
            const el = textAreaRef.current;
            if (!el) return;

            el.style.height = "auto";
            el.style.height = el.scrollHeight + "px";
        }, [textAreaRef, value]);
    }

    useAutosizeTextArea(descriptionInputRef, newRisk.description);
    useAutosizeTextArea(mitigationDescriptionInputRef, newMitigation.description);

    const selectRiskRegisterFile = () => {
        riskRegisterFile.current?.click();
    }

    const filteredRisks = useMemo(() => {
        if (searchParams.searchText === '') {
            return risks;
        }
        return risks.filter((risk) =>
            risk.userReference.toLowerCase().includes(searchParams.searchText.toLowerCase()) ||
            risk.description.toLowerCase().includes(searchParams.searchText.toLowerCase()));
    }, [searchParams, risks])

    const importRiskRegister = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result;
                if (content) {
                    FirebaseUsage.uploadFileToStorage(`risk-registers/${activeProject!.contractId}/${file.name}`, content)
                        .then((resp) => {
                            const newRiskUpload = FirebaseUsage.getBlankDoc(COLLECTIONS.RISK_UPLOADS);
                            FirebaseUsage.setDocumentWithDoc(newRiskUpload, {
                                uploadId: newRiskUpload.id,
                                contractId: activeProject!.contractId,
                                fileName: resp.metadata.name || file.name,
                                filePath: resp.metadata.fullPath || null,
                                uploadTimestamp: FirebaseUsage.timestamp(),
                                uploadedBy: user!.userId,
                                fileType: resp.metadata.contentType || file.type,
                                status: 'pending',
                            })
                                .then(() => setUploadId(newRiskUpload.id))
                                .then(() => setShowUploadModal(true))
                                .then(() => {
                                    setTimeout(() => {
                                        fetch(`https://flowledger-language-hub-854414998693.europe-west1.run.app/upload-risk-register/${newRiskUpload.id}`)
                                            .catch(e => console.log(e))
                                    }, 2000);
                                })
                                .catch((err) => console.log(err));
                        })
                }
            }
            reader.readAsArrayBuffer(file);
        }
    };

    const createRegisterWithoutAttachment = () => {
        const newRiskUpload = FirebaseUsage.getBlankDoc(COLLECTIONS.RISK_UPLOADS);
        FirebaseUsage.setDocumentWithDoc(newRiskUpload, {
            uploadId: newRiskUpload.id,
            contractId: activeProject!.contractId,
            uploadTimestamp: FirebaseUsage.timestamp(),
            uploadedBy: user!.userId,
            status: 'pending',
        })
            .then(() => setUploadId(newRiskUpload.id))
            .then(() => setShowUploadModal(true))
            .then(() => {
                setTimeout(() => {
                    fetch(`https://flowledger-language-hub-854414998693.europe-west1.run.app/create-risk-register/${newRiskUpload.id}`)
                        .catch(e => console.log(e))
                }, 2000);
            }).catch((err) => console.log(err));
    }

    const createRegisterWithAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result;
                if (content) {
                    FirebaseUsage.uploadFileToStorage(`risk-registers/${activeProject!.contractId}/${file.name}`, content)
                        .then((resp) => {
                            const newRiskUpload = FirebaseUsage.getBlankDoc(COLLECTIONS.RISK_UPLOADS);
                            FirebaseUsage.setDocumentWithDoc(newRiskUpload, {
                                uploadId: newRiskUpload.id,
                                contractId: activeProject!.contractId,
                                fileName: resp.metadata.name || file.name,
                                filePath: resp.metadata.fullPath || null,
                                uploadTimestamp: FirebaseUsage.timestamp(),
                                uploadedBy: user!.userId,
                                fileType: resp.metadata.contentType || file.type,
                                status: 'pending',
                            })
                                .then(() => setUploadId(newRiskUpload.id))
                                .then(() => setShowUploadModal(true))
                                .then(() => {
                                    setTimeout(() => {
                                        fetch(`https://convert-risk-register-854414998693.europe-west1.run.app/?uploadId=${newRiskUpload.id}`)
                                            .catch(e => console.log(e))
                                    }, 2000);
                                })
                                .catch((err) => console.log(err));
                        })
                }
            }
            reader.readAsArrayBuffer(file);
        }
    };

    const resetRiskForm = () => {
        setNewRisk({
            userReference: 'Risk-001',
            description: '',
            probability: 100,
            distribution: 'triangular',
            scheduleImpact: false,
            scheduleImpactRelative: true,
            costImpact: false,
            costImpactRelative: true,
            scheduleMin: 100,
            scheduleLikely: 150,
            scheduleMax: 200,
            costMin: 100,
            costLikely: 150,
            costMax: 200,
            mitigation: false,
            contractId: activeProject!.contractId,
            currency: 'GBP',
        });
        setNewMitigation({
            description: '',
            probability: 100,
            distribution: 'triangular',
            scheduleImpact: false,
            scheduleImpactRelative: true,
            costImpact: false,
            costImpactRelative: true,
            scheduleMin: 100,
            scheduleLikely: 150,
            scheduleMax: 200,
            costMin: 100,
            costLikely: 150,
            costMax: 200,
        });
    }

    const validRisk = useMemo(() => {
        const validMiitigation = newRisk.mitigation ?
            (newMitigation.description.trim() !== '' &&
            (newMitigation.scheduleImpact || newMitigation.costImpact) &&
                (!newMitigation.scheduleImpact ||
                    (newMitigation.scheduleLikely >= 0 && newMitigation.scheduleMax >= 0 &&
                    newMitigation.scheduleMin <= newMitigation.scheduleLikely && newMitigation.scheduleLikely <= newMitigation.scheduleMax)) &&
                (!newMitigation.costImpact ||
                    (newMitigation.costLikely >= 0 && newMitigation.costMax >= 0 &&
                    newMitigation.costMin <= newMitigation.costLikely && newMitigation.costLikely <= newMitigation.costMax)))
            : true;
        if (!validMiitigation) return false;

        return newRisk.userReference.trim() !== '' && newRisk.description.trim() !== '' &&
            (newRisk.scheduleImpact || newRisk.costImpact) &&
            (!newRisk.scheduleImpact ||
                (newRisk.scheduleLikely >= 0 && newRisk.scheduleMax >= 0 &&
                newRisk.scheduleMin <= newRisk.scheduleLikely && newRisk.scheduleLikely <= newRisk.scheduleMax)) &&
            (!newRisk.costImpact ||
                (newRisk.costLikely >= 0 && newRisk.costMax >= 0 &&
                newRisk.costMin <= newRisk.costLikely && newRisk.costLikely <= newRisk.costMax));
    }, [newRisk, newMitigation]);

    async function handleSaveNewRisk() {
        if (!validRisk) return;
        let riskToSave = {...newRisk, riskId: ''};
        const newRiskDoc = FirebaseUsage.getBlankDoc('risks');
        riskToSave.riskId = newRiskDoc.id;
        return await FirebaseUsage.setDocumentWithDoc(newRiskDoc, riskToSave);
    }

    function handleEditRisk(risk: RiskModel) {
        setNewRisk({
            userReference: risk.userReference,
            description: risk.description,
            probability: risk.probability,
            distribution: risk.distribution,
            scheduleImpact: risk.scheduleImpact,
            scheduleImpactRelative: risk.scheduleImpactRelative || !(!risk.scheduleMinPercentage),
            costImpact: risk.costImpact,
            costImpactRelative: risk.costImpactRelative || !(!risk.costMinPercentage),
            scheduleMin: risk.scheduleMin || 100,
            scheduleLikely: risk.scheduleLikely || 150,
            scheduleMax: risk.scheduleMax || 200,
            costMin: risk.costMin || 100,
            costLikely: risk.costLikely || 150,
            costMax: risk.costMax || 200,
            mitigation: risk.mitigation,
            contractId: activeProject!.contractId,
            currency: risk.currency || 'GBP',
        })
        setNewMitigation({
            description: risk.mitigationDescription || '',
            probability: risk.mitigationProbability || 100,
            distribution: risk.mitigationDistribution || 'triangular',
            scheduleImpact: risk.mitigationScheduleImpact || false,
            scheduleImpactRelative: risk.mitigationScheduleImpactRelative || true,
            costImpact: risk.mitigationCostImpact || false,
            costImpactRelative: risk.mitigationCostImpactRelative || true,
            scheduleMin: risk.mitigationScheduleMin || 100,
            scheduleLikely: risk.mitigationScheduleLikely || 150,
            scheduleMax: risk.mitigationScheduleMax || 200,
            costMin: risk.mitigationCostMin || 100,
            costLikely: risk.mitigationCostLikely || 150,
            costMax: risk.mitigationCostMax || 200,
        })

        setEditingRisk(true);
        setAddingRisk(true);
    }

    const newRiskRowForm = () => {
        return (
            <Table.Row>
                <Table.Cell>
                    <input
                        className={styles.RiskTextInput}
                        type="text"
                        value={newRisk.userReference}
                        onChange={(e) => setNewRisk({...newRisk, userReference: e.target.value})}
                    />
                </Table.Cell>
                <Table.Cell>
                    <textarea
                        ref={descriptionInputRef}
                        // onInput={handleDescriptionInput}
                        // onFocus={handleDescriptionInput}
                        placeholder="Risk description"
                        className={styles.RiskTextAreaInput}
                        autoFocus={true}
                        value={newRisk.description}
                        onChange={(e) => setNewRisk({...newRisk, description: e.target.value})}
                    />
                </Table.Cell>
                <Table.Cell>
                    <input
                        className={styles.RiskProbabilityInput}
                        type="number"
                        value={newRisk.probability}
                        onChange={(e) => setNewRisk({...newRisk, probability: parseFloat(e.target.value)})}
                    />
                </Table.Cell>
                <Table.Cell>
                    <select
                        className={styles.RiskTextInput}
                        value={newRisk.distribution}
                        onChange={(e) => setNewRisk({...newRisk, distribution: e.target.value})}
                    >
                        <option value="triangular">Triangular</option>
                        <option value="betapert">Beta-PERT</option>
                        <option value="uniform">Uniform</option>
                    </select>
                </Table.Cell>
                <Table.Cell>
                    <input
                        className={styles.RiskTextInput}
                        type="checkbox"
                        checked={newRisk.scheduleImpact}
                        onChange={(e) => setNewRisk({...newRisk, scheduleImpact: e.target.checked})}
                    />
                </Table.Cell>
                <Table.Cell>
                    {newRisk.scheduleImpact && <input
                        className={styles.RiskTextInput}
                        type="checkbox"
                        checked={newRisk.scheduleImpactRelative}
                        onChange={(e) => setNewRisk({...newRisk, scheduleImpactRelative: e.target.checked})}
                    />}
                </Table.Cell>
                <Table.Cell>
                    {newRisk.scheduleImpact && (
                        <span className={styles.RiskImpactInput}>
                            <input
                                className={styles.RiskNumberInput}
                                type="number"
                                value={newRisk.scheduleMin}
                                onBlur={(e) => setNewRisk({...newRisk,
                                    scheduleMin: Math.min(
                                        parseFloat(e.target.value),
                                        newRisk.scheduleLikely)
                                })}
                                onChange={(e) => setNewRisk({...newRisk,
                                    scheduleMin: parseFloat(e.target.value)})}
                            />
                            {newRisk.scheduleImpactRelative ? '%' : <>&nbsp;{` days`}</>}
                        </span>)}
                </Table.Cell>
                <Table.Cell>
                    {newRisk.scheduleImpact && (
                    <span className={styles.RiskImpactInput}>
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newRisk.scheduleLikely}
                            onBlur={(e) => setNewRisk({...newRisk,
                                scheduleLikely: parseFloat(e.target.value) < newRisk.scheduleMin ?
                                    newRisk.scheduleMin :
                                    parseFloat(e.target.value) > newRisk.scheduleMax ?
                                        newRisk.scheduleMax :
                                        parseFloat(e.target.value)
                            })}
                            onChange={(e) => setNewRisk({...newRisk,
                                scheduleLikely: parseFloat(e.target.value)})}
                        />
                        {newRisk.scheduleImpactRelative ? '%' : <>&nbsp;{` days`}</>}
                    </span>)}
                </Table.Cell>
                <Table.Cell>
                    {newRisk.scheduleImpact && (
                    <span className={styles.RiskImpactInput}>
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newRisk.scheduleMax}
                            onBlur={(e) => setNewRisk({...newRisk,
                                scheduleMax: Math.max(
                                    parseFloat(e.target.value),
                                    newRisk.scheduleLikely)
                            })}
                            onChange={(e) => setNewRisk({...newRisk,
                                scheduleMax: parseFloat(e.target.value)})}
                        />
                        {newRisk.scheduleImpactRelative ? '%' : <>&nbsp;{` days`}</>}
                    </span>)}
                </Table.Cell>
                <Table.Cell>
                    <input
                        className={styles.RiskTextInput}
                        type="checkbox"
                        checked={newRisk.costImpact}
                        onChange={(e) => setNewRisk({...newRisk, costImpact: e.target.checked})}
                    />
                </Table.Cell>
                <Table.Cell>
                    {newRisk.costImpact && (
                    <input
                        className={styles.RiskTextInput}
                        type="checkbox"
                        checked={newRisk.costImpactRelative}
                        onChange={(e) => setNewRisk({...newRisk, costImpactRelative: e.target.checked})}
                    />)}
                </Table.Cell>
                <Table.Cell>
                    {newRisk.costImpact && (<select
                        className={styles.RiskTextInput}
                        value={newRisk.currency || 'GBP'}
                        onChange={(e) => setNewRisk({...newRisk, currency: e.target.value})}
                    >
                        <option value="GBP">GBP</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                    </select>)}
                </Table.Cell>
                <Table.Cell>
                    {newRisk.costImpact && (
                        <span className={styles.RiskImpactInput}>
                            {!newRisk.costImpactRelative ? getCurrencySymbol(newRisk.currency) : ''}
                            <input
                                className={styles.RiskNumberInput}
                                type="number"
                                value={newRisk.costMin}
                                onChange={(e) => setNewRisk({...newRisk,
                                    costMin: Math.min(
                                        parseFloat(e.target.value),
                                        newRisk.costLikely)
                                })}
                            />
                            {newRisk.costImpactRelative ? '%' : ''}
                        </span>)}
                </Table.Cell>
                <Table.Cell>
                    {newRisk.costImpact && (
                        <span className={styles.RiskImpactInput}>
                            {!newRisk.costImpactRelative ? getCurrencySymbol(newRisk.currency) : ''}
                            <input
                                className={styles.RiskNumberInput}
                                type="number"
                                value={newRisk.costLikely}
                                onChange={(e) => setNewRisk({...newRisk,
                                    costLikely: parseFloat(e.target.value) < newRisk.costMin ?
                                        newRisk.costMin :
                                        parseFloat(e.target.value) > newRisk.costMax ?
                                            newRisk.costMax :
                                            parseFloat(e.target.value)
                                })}
                            />
                            {newRisk.costImpactRelative ? '%' : ''}
                        </span>)}
                </Table.Cell>
                <Table.Cell>
                    {newRisk.costImpact && (
                    <input
                        className={styles.RiskNumberInput}
                        type="number"
                        value={newRisk.costMax}
                        onChange={(e) => setNewRisk({...newRisk,
                            costMax: Math.max(
                                parseFloat(e.target.value),
                                newRisk.costLikely)
                        })}
                    />)}
                </Table.Cell>
                <Table.Cell>
                    <input
                        className={styles.RiskTextInput}
                        type="checkbox"
                        disabled={!newRisk.costImpact && !newRisk.scheduleImpact}
                        checked={newRisk.mitigation}
                        onChange={(e) => setNewRisk({...newRisk, mitigation: e.target.checked})}
                    />
                </Table.Cell>
            </Table.Row>
        )
    }

    const newMitigationRowForm = () => {
        return (
            <Table.Row>
                <Table.Cell style={{color: 'lightgray'}}>
                    {newRisk.userReference + '-M1'}
                </Table.Cell>
                <Table.Cell>
                    <textarea
                        ref={mitigationDescriptionInputRef}
                        // onInput={handleMitigationDescriptionInput}
                        // onFocus={handleMitigationDescriptionInput}
                        placeholder="Mitigation description"
                        className={styles.RiskTextAreaInput}
                        value={newMitigation.description}
                        onChange={(e) => setNewMitigation({...newMitigation, description: e.target.value})}
                    />
                </Table.Cell>
                <Table.Cell>
                    <input
                        className={styles.RiskNumberInput}
                        type="number"
                        value={newMitigation.probability}
                        onChange={(e) => setNewMitigation({...newMitigation, probability: parseFloat(e.target.value)})}
                    />
                </Table.Cell>
                <Table.Cell>
                    <select
                        className={styles.RiskTextInput}
                        value={newMitigation.distribution}
                        onChange={(e) => setNewMitigation({...newMitigation, distribution: e.target.value})}
                    >
                        <option
                            value="triangular"
                        >Triangular</option>
                        <option value="betapert">Beta-PERT</option>
                        <option value="uniform">Uniform</option>
                    </select>
                </Table.Cell>
                <Table.Cell>
                    <input
                        className={styles.RiskTextInput}
                        type="checkbox"
                        checked={newMitigation.scheduleImpact}
                        onChange={(e) => setNewMitigation({...newMitigation, scheduleImpact: e.target.checked})}
                    />
                </Table.Cell>
                <Table.Cell>
                    {newMitigation.scheduleImpact && (
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newMitigation.scheduleMin}
                            onChange={(e) => setNewMitigation({...newMitigation,
                                scheduleMin: Math.min(
                                    parseFloat(e.target.value),
                                    newMitigation.scheduleLikely)
                            })}
                        />)}
                </Table.Cell>
                <Table.Cell>
                    {newMitigation.scheduleImpact && (
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newMitigation.scheduleLikely}
                            onChange={(e) => setNewMitigation({...newMitigation,
                                scheduleLikely: parseFloat(e.target.value) < newMitigation.scheduleMin ?
                                    newMitigation.scheduleMin :
                                    parseFloat(e.target.value) > newMitigation.scheduleMax ?
                                        newMitigation.scheduleMax :
                                        parseFloat(e.target.value)
                            })}
                        />)}
                </Table.Cell>
                <Table.Cell>
                    {newMitigation.scheduleImpact && (
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newMitigation.scheduleMax}
                            onChange={(e) => setNewMitigation({...newMitigation,
                                scheduleMax: Math.max(
                                    parseFloat(e.target.value),
                                    newMitigation.scheduleLikely)
                            })}
                        />)}
                </Table.Cell>
                <Table.Cell></Table.Cell>

                <Table.Cell>
                    <input
                        className={styles.RiskTextInput}
                        type="checkbox"
                        checked={newMitigation.costImpact}
                        onChange={(e) => setNewMitigation({...newMitigation, costImpact: e.target.checked})}
                    />
                </Table.Cell>
                <Table.Cell>
                    {newMitigation.costImpact && (
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newMitigation.costMin}
                            onChange={(e) => setNewMitigation({...newMitigation,
                                costMin: Math.min(
                                    parseFloat(e.target.value),
                                    newMitigation.costLikely)
                            })}
                        />)}
                </Table.Cell>
                <Table.Cell>
                    {newMitigation.costImpact && (
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newMitigation.costLikely}
                            onChange={(e) => setNewMitigation({...newMitigation,
                                costLikely: parseFloat(e.target.value) < newMitigation.costMin ?
                                    newMitigation.costMin :
                                    parseFloat(e.target.value) > newMitigation.costMax ?
                                        newMitigation.costMax :
                                        parseFloat(e.target.value)
                            })}
                        />)}
                </Table.Cell>
                <Table.Cell>
                    {newMitigation.costImpact && (
                        <input
                            className={styles.RiskNumberInput}
                            type="number"
                            value={newMitigation.costMax}
                            onChange={(e) => setNewMitigation({...newMitigation,
                                costMax: Math.max(
                                    parseFloat(e.target.value),
                                    newMitigation.costLikely
                                )})}
                        />)}
                </Table.Cell>
                <Table.Cell>
                </Table.Cell>
            </Table.Row>
        )
    }

    const getCurrencySymbol = (currencyCode: string) => {
        switch (currencyCode) {
            case 'USD':
                return '$';
            case 'EUR':
                return '€';
            case 'GBP':
                return '£';
            case null:
                return '£';
            default:
                return '';
        }
    }

    async function closeRisk (risk: RiskModel) {
        return await FirebaseUsage.updateDoc(COLLECTIONS.RISKS, risk.riskId, {
            closed: true,
            closedTimestamp: FirebaseUsage.timestamp(),
            closedBy: user!.userId,
        });
    }

    async function reopenRisk (risk: RiskModel) {
        return await FirebaseUsage.updateDoc(COLLECTIONS.RISKS, risk.riskId, {
            closed: false,
            closedTimestamp: null,
            closedBy: null,
        });
    }

    return (
        <div className={styles.RiskTableContainer} style={addingRisk ? {overflowY: 'hidden'} : {}}>
            <Table>
                <Table.Header>
                    <Table.Row style={{position: 'sticky', top: 0}}>
                        <Table.HeaderCell width={1}>Risk ID</Table.HeaderCell>
                        <Table.HeaderCell width={4}>Description</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Probability (%)</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Distribution</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Tasks</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Schedule Impact</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Relative</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Schedule Min.</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Schedule Likely</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Schedule Max.</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Cost Impact</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Relative</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Currency</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Cost Min.</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Cost Likely</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Cost Max.</Table.HeaderCell>
                        <Table.HeaderCell width={1} textAlign={'center'}>Mitigation</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {/* Risk rows would be dynamically generated here */}
                    {!addingRisk ?
                        <Table.Row>
                            <Table.Cell colSpan={16} textAlign="center">
                                <Button as='div' onClick={() => {
                                    setAddingRisk(true)
                                    setSelectedRisk(null)
                                }}>
                                    <Icon name="plus" />
                                    Add New Risk
                                </Button>
                            </Table.Cell>
                        </Table.Row> :
                        newRiskRowForm()}
                    {addingRisk && newRisk.mitigation && (newRisk.costImpact || newRisk.scheduleImpact) &&
                    newMitigationRowForm()}
                    {addingRisk && (
                        <Table.Row>
                            <Table.Cell colSpan={16} textAlign="center">
                                <div className={styles.RiskAddActions}>
                                    <span>
                                        <input
                                            type="file"
                                            accept=".csv, .pdf, .txt"
                                            style={{ display: 'none' }}
                                            ref={riskRegisterFile}
                                            onChange={importRiskRegister}
                                        />
                                        <Button
                                            as='div'
                                            onClick={() => selectRiskRegisterFile()}
                                            primary
                                        >
                                            <Icon name="wizard" />
                                            Import risk register with AI
                                        </Button>
                                        <Button
                                            as='div'
                                            positive
                                            onClick={() => setShowCreateModal(true)}
                                            >
                                            <Icon name="wizard" />
                                            Create new risk register with AI
                                        </Button>
                                    </span>
                                    <span>
                                        <Button
                                            as='div'
                                            onClick={() => handleSaveNewRisk()}
                                            disabled={!validRisk}
                                            positive
                                        >
                                            <Icon name="save" />
                                            Save
                                        </Button>
                                        <Button as='div' onClick={() => {
                                            setAddingRisk(false)
                                            resetRiskForm();
                                        }}>
                                            <Icon name="trash" />
                                            Cancel
                                        </Button>
                                    </span>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )}
                    {filteredRisks.length > 0 && filteredRisks
                        .sort((a, b) => a.userReference.localeCompare(b.userReference))
                        .reduce((list, risk: RiskModel) => {
                            list.push(
                                <Table.Row
                                    key={risk.riskId}
                                    onClick={() => setSelectedRisk(prevState => prevState && prevState.riskId === risk.riskId ? null : risk)}
                                    className={addingRisk ? styles.DimmedRow :
                                        selectedRisk && selectedRisk.riskId === risk.riskId ?
                                            styles.SelectedRiskRow : risk.closed ?
                                            styles.ClosedRiskRow : styles.RiskRow}
                                >
                                    <Table.Cell>
                                        {risk.userReference}
                                    </Table.Cell>
                                    <Table.Cell>
                                            {risk.description}
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.probability}
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.distribution}
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.tasks ? risk.tasks.length : 0}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <input
                                            className={styles.RiskTextInput}
                                            type="checkbox"
                                            readOnly
                                            checked={risk.scheduleImpact}
                                            disabled={addingRisk}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <input
                                            className={styles.RiskTextInput}
                                            type="checkbox"
                                            readOnly
                                            checked={risk.scheduleImpactRelative || !(!risk.scheduleMinPercentage)}
                                            disabled={addingRisk}
                                        />
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.scheduleImpact && risk.scheduleMin ? `${risk.scheduleMin} days` :
                                            risk.scheduleMinPercentage ? `${risk.scheduleMinPercentage}%` : ''}
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.scheduleImpact && risk.scheduleLikely ? `${risk.scheduleLikely} days` :
                                            risk.scheduleLikelyPercentage ? `${risk.scheduleLikelyPercentage}%` : ''}
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.scheduleImpact && risk.scheduleMax ? `${risk.scheduleMax} days` :
                                            risk.scheduleMaxPercentage ? `${risk.scheduleMaxPercentage}%` : ''}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <input
                                            className={styles.RiskTextInput}
                                            type="checkbox"
                                            readOnly
                                            checked={risk.costImpact}
                                            disabled={addingRisk}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <input
                                            className={styles.RiskTextInput}
                                            type="checkbox"
                                            readOnly
                                            checked={risk.costImpactRelative || !(!risk.costMinPercentage)}
                                            disabled={addingRisk}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>{risk.currency || 'GBP'}</Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.costImpact && risk.costMin ? `${getCurrencySymbol(risk.currency)}${risk.costMin.toLocaleString()}` :
                                            risk.costMinPercentage ? `${risk.costMinPercentage}%` : ''}
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.costImpact && risk.costLikely ? `${getCurrencySymbol(risk.currency)}${risk.costLikely.toLocaleString()}` :
                                            risk.costLikelyPercentage ? `${risk.costLikelyPercentage}%` : ''}
                                    </Table.Cell>
                                    <Table.Cell textAlign={'center'}>
                                        {risk.costImpact && risk.costMax ? `${getCurrencySymbol(risk.currency)}${risk.costMax.toLocaleString()}` :
                                            risk.costMaxPercentage ? `${risk.costMaxPercentage}%` : ''}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <input
                                            className={styles.RiskTextInput}
                                            type="checkbox"
                                            readOnly
                                            checked={risk.mitigation}
                                            disabled={addingRisk}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            );
                            if (risk.mitigation && selectedRisk && selectedRisk.riskId === risk.riskId) {
                                list.push(
                                    <Table.Row
                                        key={risk.riskId + '-mitigation'}
                                        className={addingRisk ? styles.DimmedRow :
                                            selectedRisk && selectedRisk.riskId === risk.riskId ?
                                                styles.SelectedRiskRow : styles.RiskRow}
                                    >
                                        <Table.Cell style={{color: 'lightgray'}}>
                                            {risk.userReference + '-M1'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {risk.mitigationDescription}
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationProbability}
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationDistribution}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <input
                                                className={styles.RiskTextInput}
                                                type="checkbox"
                                                checked={risk.mitigationScheduleImpact}
                                                readOnly
                                                disabled={addingRisk}
                                            />
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationScheduleImpact && risk.mitigationScheduleMin ? `${risk.mitigationScheduleMin} days` :
                                                risk.mitigationScheduleMinPercentage ? `${risk.mitigationScheduleMinPercentage}%` : ''}
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationScheduleImpact && risk.mitigationScheduleLikely ? `${risk.mitigationScheduleLikely} days` :
                                                risk.mitigationScheduleLikelyPercentage ? `${risk.mitigationScheduleLikelyPercentage}%` : ''}
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationScheduleImpact && risk.mitigationScheduleMax ? `${risk.mitigationScheduleMax} days` :
                                                risk.mitigationScheduleMaxPercentage ? `${risk.mitigationScheduleMaxPercentage}%` : ''}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <input
                                                className={styles.RiskTextInput}
                                                type="checkbox"
                                                checked={risk.mitigationCostImpact}
                                                readOnly
                                                disabled={addingRisk}
                                            />
                                        </Table.Cell>
                                        <Table.Cell></Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationCostImpact && risk.mitigationCostMin ? `${getCurrencySymbol(risk.currency)}${risk.mitigationCostMin.toLocaleString()}` :
                                                risk.mitigationCostMinPercentage ? `${risk.mitigationCostMinPercentage}%` : ''}
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationCostImpact && risk.mitigationCostLikely ? `${getCurrencySymbol(risk.currency)}${risk.mitigationCostLikely.toLocaleString()}` :
                                                risk.mitigationCostLikelyPercentage ? `${risk.mitigationCostLikelyPercentage}%` : ''}
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'}>
                                            {risk.mitigationCostImpact && risk.mitigationCostMax ? `${getCurrencySymbol(risk.currency)}${risk.mitigationCostMax.toLocaleString()}` :
                                                risk.mitigationCostMaxPercentage ? `${risk.mitigationCostMaxPercentage}%` : ''}
                                        </Table.Cell>
                                        <Table.Cell>
                                        </Table.Cell>
                                    </Table.Row>
                                )
                                if (selectedRisk && selectedRisk.riskId === risk.riskId) {
                                    list.push(
                                        <Table.Row key={risk.riskId + '-mitigation-actions'} className={styles.RiskActions}>
                                            <Table.Cell colSpan={16} textAlign="right">
                                                <div className={styles.RiskMitigationActions}>
                                                    <Button
                                                        as='div'
                                                        onClick={() => {
                                                            dispatch(projectActions.Actions.setRiskMapping(selectedRisk?.riskId || ''))
                                                            dispatch(projectActions.Actions.setView('gantt'))
                                                        }}
                                                        primary
                                                    >
                                                        <Icon name="tasks" />
                                                        Task Mapping
                                                    </Button>
                                                    <Button
                                                        as='div'
                                                        onClick={() => handleEditRisk(risk)}
                                                    >
                                                        <Icon name="edit" />
                                                        Edit Risk
                                                    </Button>
                                                    {!risk.closed ? <Button
                                                        as='div'
                                                        onClick={() => closeRisk(risk).catch(console.error)}
                                                        positive
                                                    >
                                                        <Icon name="check square" />
                                                        Mark Risk as Closed
                                                    </Button> :
                                                        <Button
                                                            as='div'
                                                            onClick={() => reopenRisk(risk).catch(console.error)}
                                                            color='yellow'
                                                        >
                                                            <Icon name="square outline" />
                                                            Reopen Risk
                                                        </Button>}
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    )
                                }
                            }
                            return list;
                        }, [] as JSX.Element[])
                    }
                </Table.Body>
            </Table>
            {showUploadModal && <RiskUploadModal riskUploadId={uploadId} setShowUploadModal={setShowUploadModal} />}
            {showCreateModal &&
            <Modal size='small' open closeIcon onClose={() => setShowCreateModal(false)}>
                <Modal.Header>Create Risk Register with AI</Modal.Header>
                <Modal.Content>Attach a file to provide more context about the project?</Modal.Content>
                <Modal.Actions>
                    <Button
                        as='div'
                        onClick={() => setShowCreateModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        as='div'
                        primary
                        onClick={() => createRegisterWithoutAttachment()}
                    >
                        Continue without file
                    </Button>
                    <input
                        type="file"
                        accept=".csv, .pdf, .txt"
                        style={{ display: 'none' }}
                        ref={riskRegisterFile}
                        onChange={createRegisterWithAttachment}
                    />
                    <Button
                        as='div'
                        positive
                        onClick={() => selectRiskRegisterFile()}
                    >
                        Attach a file
                    </Button>
                </Modal.Actions>
            </Modal>}
        </div>
    )
}